import type { ImageListQuery, ImageSort } from "@shared/types";

const SELECT_CLASS = "field-sm select-chevron w-auto flex-shrink-0 cursor-pointer";

const TYPES = [
  ["", "All types"],
  ["jpeg", "JPEG"],
  ["png", "PNG"],
  ["webp", "WebP"],
  ["gif", "GIF"],
  ["avif", "AVIF"],
  ["svg", "SVG"],
] as const;

const SIZES = [
  ["", "Any size"],
  ["lt1", "Under 1 MB"],
  ["1to5", "1 to 5 MB"],
  ["5to10", "5 to 10 MB"],
  ["gt10", "Over 10 MB"],
] as const;

const SORTS: [ImageSort, string][] = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["name-asc", "Name A to Z"],
  ["name-desc", "Name Z to A"],
  ["largest", "Largest first"],
  ["smallest", "Smallest first"],
];

interface Props {
  query: ImageListQuery;
  onChange: (patch: Partial<ImageListQuery>) => void;
  recursiveVisible: boolean;
}

export function GridControls({ query, onChange, recursiveVisible }: Props) {
  return (
    // Selects grow with their text, so on narrow screens the row scrolls
    // sideways inside itself rather than pushing the page wider.
    <div className="scrollable -mx-4 flex max-w-full items-center gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
      <select
        className={SELECT_CLASS}
        aria-label="Filter by type"
        value={query.type ?? ""}
        onChange={(e) => onChange({ type: e.target.value || undefined, page: 1 })}
      >
        {TYPES.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>

      <select
        className={SELECT_CLASS}
        aria-label="Filter by size"
        value={query.sizeBucket ?? ""}
        onChange={(e) => onChange({ sizeBucket: (e.target.value || undefined) as ImageListQuery["sizeBucket"], page: 1 })}
      >
        {SIZES.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>

      <select
        className={SELECT_CLASS}
        aria-label="Sort images"
        value={query.sort ?? "newest"}
        onChange={(e) => onChange({ sort: e.target.value as ImageSort, page: 1 })}
      >
        {SORTS.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>

      {recursiveVisible && (
        <label className="field-sm flex flex-shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap">
          <input
            type="checkbox"
            checked={!!query.recursive}
            onChange={(e) => onChange({ recursive: e.target.checked, page: 1 })}
            className="h-3.5 w-3.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900/20"
          />
          Include subfolders
        </label>
      )}
    </div>
  );
}
